import React from 'react';
import { Redirect, Route, RouteProps, Switch, withRouter } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  AUTH_PREFIX_PATH,
  COMMUNITY_PREFIX_PATH,
  INVITATION_PREFIX_PATH,
  VENDORLINK_PREFIX_PATH,
} from 'configs/AppConfig';
import InvitationLayout from 'layouts/invitation-layout';
import LoadProfile from './LoadProfile';
import { setSignInRedirect } from 'redux/actions';
import Loading from 'components/shared-components/Loading';
import { env } from '../configs/EnvironmentConfig';
import CommunityAppViews from 'views/app-views';
import VendorLinkAppViews from 'views/vendorlink-views';
import AuthViews from './auth-views';
import { useAuthState } from '../redux/selector';
import useBodyClass from '../hooks/useBodyClass';
import { IntlProvider } from 'react-intl';
import AppLocale from '../lang';
import { ConfigProvider } from 'antd';
import enUSIntl from 'antd/lib/locale/en_US';

const AuthenticatedRoute: React.FC<React.PropsWithChildren<RouteProps>> = ({ children, ...rest }) => {
  const dispatch = useDispatch();
  const { token } = useAuthState();
  return (
    <Route
      {...rest}
      render={({ location }) => {
        if (token) {
          return children;
        }
        dispatch(setSignInRedirect(location.pathname, location.search));
        return <Redirect to={{ pathname: AUTH_PREFIX_PATH }} />;
      }}
    />
  );
};

export const Views = () => {
  // Some layout classes depend on this. e.g. `mr-5`.
  useBodyClass('dir-ltr');
  const { initialized } = useAuthState();
  if (!initialized) {
    return <Loading cover="page" />;
  }
  const currentAppLocale = AppLocale['en'];
  return (
    <ConfigProvider locale={enUSIntl}>
      <IntlProvider locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
        <Switch>
          <Route exact path="/">
            <Redirect to={COMMUNITY_PREFIX_PATH} />
          </Route>
          <AuthenticatedRoute path={INVITATION_PREFIX_PATH}>
            <LoadProfile>
              <InvitationLayout />
            </LoadProfile>
          </AuthenticatedRoute>
          <Route path={AUTH_PREFIX_PATH}>
            <AuthViews />
          </Route>
          <AuthenticatedRoute path={COMMUNITY_PREFIX_PATH}>
            <LoadProfile>
              <CommunityAppViews />
            </LoadProfile>
          </AuthenticatedRoute>
          <AuthenticatedRoute path={VENDORLINK_PREFIX_PATH}>
            <LoadProfile>
              <VendorLinkAppViews />
            </LoadProfile>
          </AuthenticatedRoute>
          {/*
           This is a temporary solution to redirect `/privacy` to a statically hosted privacy notice HTML file,
           inspired by https://stackoverflow.com/questions/42914666/react-router-external-link
           TODO: Configure routing at the S3 level or make the page a part of the app.
           */}
          <Route
            exact
            path="/privacy"
            render={() => (window.location.href = `${env.REACT_APP_WEB_URL}/privacy.html`)}
          />
          <Redirect to={COMMUNITY_PREFIX_PATH} />
        </Switch>
      </IntlProvider>
    </ConfigProvider>
  );
};

export default withRouter(Views);
